import React from 'react';
import { capitalize } from 'lodash';
import { FormControl, MenuItem, Select, Chip, Box } from '@mui/material';
import { useField } from 'formik';
import { ISelectOption } from '../../types/sharedFormTypes';
import { IconArrowDown } from '../../utils/icons';
import { IExpertQuestions, IVideoContent } from './constants';
import { FieldError } from '../fields/FieldError/FieldError';

interface Props {
  disabled?: boolean;
  isMultiple?: boolean;
  name: keyof IVideoContent | keyof IExpertQuestions;
  placeholder?: string;
  options?: Array<string>;
  objectOptions?: Array<ISelectOption>;
  icon?: JSX.Element;
}
export function FieldSelect(props: Props) {
  const { name, placeholder, options, objectOptions, isMultiple, icon } = props;
  const [field, meta] = useField(name);

  return (
    <FormControl className="flex flex-col gap-2 w-full relative" fullWidth>
      <Select
        multiple={isMultiple}
        {...field}
        size="small"
        className="bg-surface-2 !rounded-lg"
        placeholder={placeholder}
        displayEmpty
        sx={{
          '& fieldset': { border: 'none' },
        }}
        IconComponent={(_props) => {
          const rotate = _props.className.toString().includes('iconOpen');
          return (
            <div
              style={{
                position: 'absolute',
                cursor: 'pointer',
                pointerEvents: 'none',
                right: 10,
                transform: rotate && !icon ? 'rotate(180deg)' : 'none',
              }}
            >
              {icon ?? <IconArrowDown />}
            </div>
          );
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
        renderValue={(selected) => {
          if (!selected || (Array.isArray(selected) && !selected.length)) {
            return <span className="text-muted">{placeholder}</span>;
          }
          if (objectOptions?.length) {
            return (
              objectOptions?.find((option) => option.value === selected)
                ?.label ?? selected
            );
          }
          return selected;
        }}
      >
        {options?.map((option) => (
          <MenuItem value={option} key={option}>
            {capitalize(option)}
          </MenuItem>
        ))}
        {objectOptions?.map((option, index) => (
          <MenuItem value={option.value} key={`options-list-${index}`}>
            {capitalize(option.label)}
          </MenuItem>
        ))}
      </Select>
      {!!meta.touched && !!meta.error && <FieldError>{meta.error}</FieldError>}
    </FormControl>
  );
}

export function FieldMultiSelect(props: Props) {
  const { name, placeholder, options, objectOptions, icon } = props;
  const [field, meta, helpers] = useField(name);

  // Ensure value is always an array for multi-select
  const value = Array.isArray(field.value) ? field.value : [];

  const handleChange = (event: any) => {
    helpers.setValue(event.target.value);
  };

  return (
    <FormControl className="flex flex-col gap-2 w-full relative" fullWidth>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        size="small"
        className="bg-surface-2 !rounded-lg"
        placeholder={placeholder}
        displayEmpty
        sx={{
          '& fieldset': { border: 'none' },
        }}
        IconComponent={(_props) => {
          const rotate = _props.className.toString().includes('iconOpen');
          return (
            <div
              style={{
                position: 'absolute',
                cursor: 'pointer',
                pointerEvents: 'none',
                right: 10,
                transform: rotate && !icon ? 'rotate(180deg)' : 'none',
              }}
            >
              {icon ?? <IconArrowDown />}
            </div>
          );
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
        renderValue={(selected) => {
          if (!selected || (Array.isArray(selected) && !selected.length)) {
            return <span className="text-muted">{placeholder}</span>;
          }
          // Show selected options as chips
          const selectedOptions = objectOptions?.length
            ? objectOptions.filter((option) => selected.includes(option.value))
            : (options || []).filter((option) => selected.includes(option));
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedOptions.map((option: any) => (
                <Chip
                  key={option.value || option}
                  label={capitalize(option.label || option)}
                  sx={{ background: 'var(--surface-2)', fontWeight: 500 }}
                />
              ))}
            </Box>
          );
        }}
      >
        {options?.map((option) => (
          <MenuItem value={option} key={option}>
            {capitalize(option)}
          </MenuItem>
        ))}
        {objectOptions?.map((option, index) => (
          <MenuItem value={option.value} key={`options-list-${index}`}>
            {capitalize(option.label)}
          </MenuItem>
        ))}
      </Select>
      {!!meta.touched && !!meta.error && <FieldError>{meta.error}</FieldError>}
    </FormControl>
  );
}
